import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { imgSrc } from "../../lib/siteContent";
import ImageUploadField from "./ImageUploadField";

const EMPTY = { category: "Web & Software", name: "", short: "", description: "", image: "", deliverables: [] };
const CATEGORIES = ["Creative & Design", "Web & Software", "AI & Intelligence", "Media & Marketing"];

function ServiceForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, deliverables: initial?.deliverables || [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        category: form.category, name: form.name, short: form.short,
        description: form.description, image: form.image,
        deliverables: (form.deliverables || []).map((d) => d.trim()).filter(Boolean),
      };
      if (initial?.id) await api.put(`/admin/services/${initial.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Service saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6 border border-[#00D4FF]/30" data-testid="service-form">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit service" : "New service"}</h3>
        <button onClick={onCancel} className="text-[#5A5A72] hover:text-[#0E0E1A]"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Name</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="service-name-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="service-category-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Short tag</label>
          <input value={form.short} onChange={(e) => set("short", e.target.value)} data-testid="service-short-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Description</label>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="service-description-input" className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm leading-relaxed" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Deliverables (comma separated)</label>
          <input
            value={(form.deliverables || []).join(", ")}
            onChange={(e) => set("deliverables", e.target.value.split(","))}
            data-testid="service-deliverables-input"
            className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <ImageUploadField label="Service image" value={form.image} onChange={(v) => set("image", v)} testId="service-image" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} className="btn-ghost px-4 py-2 rounded-lg text-sm">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="service-save-btn" className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

export default function AdminServicesManager() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | service
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services");
      setServices(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Service deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const move = async (idx, dir) => {
    const prev = services;
    const next = [...services];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setServices(next);
    try {
      await api.put("/admin/services/reorder", { ids: next.map((s) => s.id) });
    } catch (e) {
      setServices(prev);
      toast.error("Reorder failed");
    }
  };

  if (editing !== null) {
    return <ServiceForm initial={editing} onCancel={() => setEditing(null)} onSaved={onSaved} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5A5A72]">{services.length} services · reorder with the arrows</p>
        <button onClick={() => setEditing({})} data-testid="add-service-btn" className="btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add service
        </button>
      </div>
      {loading ? (
        <div className="glass-card rounded-2xl p-10 text-center text-[#5A5A72]">Loading…</div>
      ) : (
        services.map((s, i) => (
          <div key={s.id} data-testid={`service-row-${s.id}`} className="glass-card rounded-2xl p-4 flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#5A5A72] hover:text-[#2060EE] disabled:opacity-30" data-testid={`service-up-${s.id}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === services.length - 1} className="text-[#5A5A72] hover:text-[#2060EE] disabled:opacity-30" data-testid={`service-down-${s.id}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-black/[0.08] bg-white flex-shrink-0">
              {s.image && <img src={imgSrc(s.image)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold">{s.name}</p>
              <p className="text-xs font-mono text-[#00D4FF]">{s.category} · {s.short}</p>
              <p className="text-sm text-[#5A5A72] line-clamp-1 mt-1">{s.description}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => remove(s.id)} data-testid={`service-delete-${s.id}`} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
